import * as React from "react";
import { Pressable, StyleSheet, type ViewStyle, View } from "react-native";
import { AlertTriangle, ArrowRight, Check, User, XCircle } from "lucide-react";
import Pill from "../../../common/Pill/Pill";
import CustomText from "../../../common/general/CustomText/CustomText";
import {
  CONNECTOR_CATEGORY_LABELS,
  getConnectorAuthPresentation,
  getConnectorConnectionPattern,
  hasSavedConfiguration,
  type CatalogConnector,
  type ConnectionStatus,
} from "../catalog.utils";
import { ConnectorLogo } from "../ConnectorDetails/ConnectorLogo";

type UserConnectionAggregateSummary = {
  activeConnections: number;
  failedConnections: number;
  byRole: {
    faculty: number;
    student: number;
    admin: number;
  };
};

const PILL_TYPES: Record<
  string,
  { text: string; backgroundColor: string; textColor: string; borderColor: string; icon?: JSX.Element }
> = {
  active: {
    text: "Active",
    backgroundColor: "#F3FEE7",
    textColor: "#3B7C0F",
    borderColor: "#86EFAC40",
    icon: <Check size={12} color="#16A34A" />,
  },
  approved: {
    text: "Approved",
    backgroundColor: "#F3FEE7",
    textColor: "#3B7C0F",
    borderColor: "#86EFAC40",
    icon: <Check size={12} color="#16A34A" />,
  },
  working: {
    text: "Working",
    backgroundColor: "#EFF8FF",
    textColor: "#175CD3",
    borderColor: "#B2DDFF",
    icon: <Check size={12} color="#175CD3" />,
  },
  degraded: {
    text: "Degraded",
    backgroundColor: "#FFFAEB",
    textColor: "#B54708",
    borderColor: "#FCD34D40",
    icon: <AlertTriangle size={12} color="#D97706" />,
  },
  failed: {
    text: "Failed",
    backgroundColor: "#FEE2E2",
    textColor: "#B91C1C",
    borderColor: "#FCA5A540",
    icon: <XCircle size={12} color="#DC2626" />,
  },
  expired: {
    text: "Expired",
    backgroundColor: "#FEE2E2",
    textColor: "#B91C1C",
    borderColor: "#FCA5A540",
    icon: <XCircle size={12} color="#DC2626" />,
  },
  "personal-only": {
    text: "Personal",
    backgroundColor: "",
    textColor: "#101828",
    borderColor: "#EAECF0",
    icon: undefined,
  },
  "setup-required": {
    text: "Setup Required",
    backgroundColor: "#F2F4F7",
    textColor: "#101828",
    borderColor: "#EAECF0",
    icon: undefined,
  },
};

const getBorderColor = (connectionStatus: ConnectionStatus | null, isApproved: boolean): string => {
  if (connectionStatus === "active" || connectionStatus === "working" || isApproved) {
    return "#86EFAC40";
  }

  if (connectionStatus === "degraded") {
    return "#FCD34D40";
  }

  if (connectionStatus === "failed" || connectionStatus === "expired") {
    return "#FCA5A540";
  }

  return "#E5E7EB";
};

interface ConnectorCardProps {
  connector: CatalogConnector;
  connectionStatus: ConnectionStatus | null;
  isApproved: boolean;
  onClick: (id: string) => void;
  style?: ViewStyle;
  approval?: {
    approvedBy: string;
    userConnections?: number;
  };
  selectedTab?: string;
  healthPercentage?: number;
  aggregate?: UserConnectionAggregateSummary;
}

export function ConnectorCard({
  connector,
  connectionStatus,
  isApproved,
  onClick,
  style,
  approval,
  selectedTab = "catalog",
  healthPercentage = 0,
  aggregate = { byRole: { faculty: 0, student: 0, admin: 0 }, failedConnections: 0 },
}: ConnectorCardProps) {
  const isConnected = connectionStatus !== null || isApproved || hasSavedConfiguration(connector);
  const borderColor = getBorderColor(connectionStatus, isApproved);
  const connectionPattern = getConnectorConnectionPattern(connector);
  const authPresentation = getConnectorAuthPresentation(connector);

  return (
    <Pressable
      onPress={() => onClick(connector.id)}
      accessibilityRole="button"
      accessibilityLabel={`${connector.name!} - ${connectionStatus ? `Status: ${connectionStatus}` : "Not connected"}`}
      style={({ pressed }) => [styles.card, { borderColor }, pressed && styles.cardPressed, style]}>
      <View style={styles.header}>
        <View style={styles.logoNameRow}>
          <ConnectorLogo iconKey={connector.icon} category={connector.category} size={36} />
          <View style={styles.nameBlock}>
            <CustomText
              text={connector.name!}
              size="s"
              useTranslationText={false}
              textType="text"
              weight="bold"
              role="text"
              style={{ color: "#101828" }}
              numberOfLines={1}
            />
            {selectedTab === "activeConnections-user" ? (
              <CustomText
                text={`${aggregate.activeConnections} users connected`}
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="medium"
                role="text"
                style={{ color: "#667085", marginTop: 2 }}
              />
            ) : (
              <CustomText
                text={CONNECTOR_CATEGORY_LABELS[connector.category]}
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="medium"
                role="text"
                style={{ color: "#667085", marginTop: 2 }}
              />
            )}
          </View>
        </View>

        {connectionStatus ? (
          <Pill
            text={PILL_TYPES[connectionStatus]?.text}
            backgroundColor={PILL_TYPES[connectionStatus]?.backgroundColor}
            textColor={PILL_TYPES[connectionStatus]?.textColor}
            textSize="xs"
            updated={true}
            borderColor={PILL_TYPES[connectionStatus]?.borderColor}
            borderWidth={1}
            icon={PILL_TYPES[connectionStatus]?.icon}
          />
        ) : isApproved ? (
          <Pill
            text={PILL_TYPES.approved.text}
            backgroundColor={PILL_TYPES.approved.backgroundColor}
            textColor={PILL_TYPES.approved.textColor}
            textSize="xs"
            updated={true}
            borderColor={PILL_TYPES.approved.borderColor}
            borderWidth={1}
            icon={PILL_TYPES.approved.icon}
          />
        ) : null}
      </View>
      {selectedTab === "catalog" && (
        <View style={{ minHeight: 40, maxHeight: 40 }}>
          <CustomText
            text={connector.description!}
            size="xs"
            useTranslationText={false}
            textType="text"
            weight="normal"
            role="text"
            style={{ color: "#344054" }}
            numberOfLines={2}
          />
        </View>
      )}

      {selectedTab === "catalog" && (
        <View style={styles.footer}>
          <Pill
            text={isConnected ? "Setup Complete" : PILL_TYPES[connectionPattern]?.text}
            backgroundColor={PILL_TYPES[connectionPattern]?.backgroundColor}
            textColor={PILL_TYPES[connectionPattern]?.textColor}
            textSize="xs"
            updated={true}
            borderColor={PILL_TYPES[connectionPattern]?.borderColor}
            borderWidth={1}
            icon={PILL_TYPES[connectionPattern]?.icon}
          />

          {connectionPattern === "personal-only" ? (
            <View style={styles.ctaRow}>
              <User size={12} color="#667085" />
              <CustomText
                text="Personal only"
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="normal"
                role="text"
                style={styles.ctaMuted}
              />
            </View>
          ) : isConnected ? (
            <View style={styles.ctaRow}>
              <CustomText
                text="Manage"
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="medium"
                role="text"
                style={{ color: "#344054" }}
              />
              <ArrowRight size={12} color="#344054" />
            </View>
          ) : (
            <View style={styles.ctaRow}>
              <CustomText
                text={authPresentation.ctaLabel}
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="medium"
                role="text"
                style={{ color: "#3459D4" }}
              />
              <ArrowRight size={12} color="#2563EB" />
            </View>
          )}
        </View>
      )}
      {selectedTab === "activeConnections" && (
        <View style={styles.approvalFooter}>
          <CustomText
            text={`Approved by ${approval?.approvedBy || ""}`}
            size="xs"
            useTranslationText={false}
            textType="text"
            weight="normal"
            role="text"
            style={styles.approvedByText}
          />
          <CustomText
            text={`${approval?.userConnections || 0} users connected`}
            size="xs"
            useTranslationText={false}
            textType="text"
            weight="medium"
            role="text"
            style={styles.usersConnectedText}
          />
        </View>
      )}
      {selectedTab === "activeConnections-user" && (
        <View>
          <View>
            <View style={styles.healthHeader}>
              <CustomText
                text="Health"
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="normal"
                role="text"
                style={styles.labelMuted}
              />
              <CustomText
                text={`${healthPercentage}%`}
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="medium"
                role="text"
                style={[
                  styles.healthPct,
                  healthPercentage >= 90
                    ? styles.textSuccess
                    : healthPercentage >= 70
                      ? styles.textWarning
                      : styles.textError,
                ]}
              />
            </View>

            <View style={styles.healthTrack}>
              <View
                style={[
                  styles.healthFill,
                  { width: `${healthPercentage}%` },
                  healthPercentage >= 90
                    ? styles.bgSuccess
                    : healthPercentage >= 70
                      ? styles.bgWarning
                      : styles.bgError,
                ]}
              />
            </View>
          </View>

          <View style={styles.roleRow}>
            {aggregate.byRole.faculty > 0 && (
              <CustomText
                text={`${aggregate.byRole.faculty} faculty`}
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="normal"
                role="text"
                style={styles.roleText}
              />
            )}
            {aggregate.byRole.student > 0 && (
              <CustomText
                text={`${aggregate.byRole.student} students`}
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="normal"
                role="text"
                style={styles.roleText}
              />
            )}
            {aggregate.byRole.admin > 0 && (
              <CustomText
                text={`${aggregate.byRole.admin} admins`}
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="normal"
                role="text"
                style={styles.roleText}
              />
            )}
          </View>

          {aggregate.failedConnections > 0 && (
            <View style={styles.warningRow}>
              <AlertTriangle width={12} height={12} color="#B54708" />
              <CustomText
                text={`${aggregate.failedConnections} expired — users will be prompted to reconnect`}
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="normal"
                role="text"
                style={styles.warningText}
              />
            </View>
          )}
        </View>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderRadius: 32,
    paddingHorizontal: 16,
    paddingVertical: 14,
    gap: 12,
  },
  cardPressed: {
    opacity: 0.85,
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  logoNameRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    flex: 1,
    minWidth: 0,
  },
  nameBlock: {
    flex: 1,
    minWidth: 0,
  },
  footer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  ctaRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  ctaMuted: {
    fontSize: 11,
    color: "#667085",
  },
  approvalFooter: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  approvedByText: {
    fontSize: 12,
    color: "#667085",
  },
  usersConnectedText: {
    fontSize: 12,
    fontWeight: "500",
    color: "#344054",
  },
  healthHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  labelMuted: {
    fontSize: 12,
    color: "#667085",
  },
  healthPct: {
    fontSize: 12,
    fontWeight: "500",
  },
  textSuccess: { color: "#3B7C0F" },
  textWarning: { color: "#B54708" },
  textError: { color: "#B91C1C" },
  healthTrack: {
    width: "100%",
    height: 6,
    backgroundColor: "#F3F4F6",
    borderRadius: 9999,
    overflow: "hidden",
  },
  healthFill: {
    height: "100%",
    borderRadius: 9999,
  },
  bgSuccess: { backgroundColor: "#3B7C0F" },
  bgWarning: { backgroundColor: "#B54708" },
  bgError: { backgroundColor: "#B91C1C" },
  roleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginTop: 12,
  },
  roleText: {
    fontSize: 12,
    color: "#344054",
  },
  warningRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 12,
  },
  warningText: {
    fontSize: 12,
    color: "#B54708",
  },
});
